// Libs
import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { Box, Grid2, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
// Components, Layouts, Pages
import {
  AutoCompleteComponent,
  BaseDatePicker,
  BaseInput,
  BaseSelect,
  BaseUploadDocument,
  BaseUploadImage,
} from '~/components/common';
// Others
import { ReasonType } from '~/utils/enum/common';
import { IFormInventory } from '~/utils/interface/inventory';
import { IBaseFile, IBaseOption, IPayloadCallApiFn, IQueryParamBase, IResultCallApiFn } from '~/utils/interface/common';
import {
  DEFAULT_MUCH_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  EMPTY_STRING,
  optionProductDesignation,
  optionReason,
  REG_EXP,
} from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { ICategory, IFormCategory } from '~/utils/interface/category';
import { createCategory, getListCategory } from '~/thunks/category/categoryThunk';
import { createFormData, formatInputCurrency, removeEmptyObjects } from '~/utils/helper/common';
// Styles, images, icons
import styles from './InfoInventoryForm.module.scss';

type Props = {
  onRemoveFile?: (id: string) => void;
};

const cx = classNames.bind(styles);

const InfoInventoryForm = (props: Props) => {
  //#region Destructuring Props
  const { onRemoveFile } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/inventory', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: inventoryId } = useParams();

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<IFormInventory>();

  const { reason, timeLoading, categoryName } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const categoryReloadKey = useRef<number>(0);
  const [categoryAddName, setCategoryAddName] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setValue('timeUnloading', timeLoading);
  }, [timeLoading]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleAddNewCategory = async (option: IBaseOption) => {
    const dataCategory: IFormCategory = {
      name: option.value,
    };

    const formData = await createFormData(removeEmptyObjects(dataCategory), ['categoryImage']);

    dispatch(createCategory(formData))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        setValue('categoryId', res?.data?.id);
        setCategoryAddName(res?.data?.name || EMPTY_STRING);
        categoryReloadKey.current += 1;
      })
      .catch((_error) => {})
      .finally(() => {});
  };

  const fetchOptionsProductDesignation = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionProductDesignation,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsReason = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionReason,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsCategory = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
      const { page, searchKey } = payload;
      const payloadParams: IQueryParamBase = {
        page: page,
        limit: DEFAULT_MUCH_PAGE,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListCategory(payloadParams)).unwrap();

        const { responses: listCategory, pagination } = resp?.data;

        const formatOptions: IBaseOption[] = handleConvertOptionCategory(listCategory);

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const handleConvertOptionCategory = (data: ICategory[]): IBaseOption[] => {
    const options: IBaseOption[] = data.map((item) => ({
      value: item.id,
      label: item.name,
    }));

    return options;
  };

  const handleRemoveFile = (file: IBaseFile) => {
    if (!file.id) return;

    onRemoveFile?.(file.id);
  };
  //#endregion Handle Function

  return (
    <Box id='inventoryFormPage' className={cx('container')}>
      <Typography className={cx('header')}>{t('inventory_form_title_product_details')}</Typography>

      <Grid2 id='infoInventoryFormComponent' columnSpacing={6} rowSpacing={'15px'} container>
        <Grid2 size={4}>
          <Controller
            control={control}
            name={'productId'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('inventory_form_title_product_id')}
                  placeholder={t('inventory_form_title_enter_product_id')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.productId?.message}
                  required
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'name'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('inventory_form_title_name')}
                  placeholder={t('inventory_form_title_enter_name')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.name?.message}
                  required
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'brand'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('inventory_form_title_brand')}
                  placeholder={t('inventory_form_title_enter_brand_name')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.brand?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'categoryId'}
            render={({ field: { onChange, value } }) => {
              return (
                <AutoCompleteComponent
                  isAddNew
                  height={38}
                  multiple={false}
                  callApiFn={fetchOptionsCategory}
                  itemNameDefault={categoryAddName || categoryName}
                  refreshKey={categoryReloadKey.current}
                  value={value}
                  onChange={(selected) => {
                    if (Array.isArray(selected)) return;

                    onChange(selected?.value);
                  }}
                  onAddNewOption={handleAddNewCategory}
                  label={t('inventory_form_title_category')}
                  errorMessage={errors?.categoryId?.message}
                  required
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'productDesignation'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseSelect
                  gap={10}
                  height={36}
                  styleType='contained'
                  label={t('inventory_form_title_product_designation')}
                  placeholder={t('inventory_form_title_select_designation')}
                  callApiFn={fetchOptionsProductDesignation}
                  value={value}
                  onChange={(data) => onChange(data?.value)}
                  errorMessage={errors?.productDesignation?.message}
                  required
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'weight'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('inventory_form_title_weight')}
                  placeholder={t('common_text_placeholder_currency', { ns: 'common/base' })}
                  value={value}
                  onChange={(event) => {
                    onChange(formatInputCurrency(event.target.value));
                  }}
                  errorMessage={errors?.weight?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'dimensions'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('inventory_form_title_dimensions')}
                  placeholder={t('inventory_form_title_lwh_inches')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.dimensions?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'warehouseLocation'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('inventory_form_title_warehouse_location')}
                  placeholder={t('inventory_form_title_warehouse_location_placeholder')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.warehouseLocation?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'note'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('inventory_form_title_internal_notes')}
                  placeholder={t('inventory_form_title_internal_notes_only')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.note?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'description'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('inventory_form_title_description')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.description?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={8}></Grid2>

        <Grid2 size={6}>
          <Controller
            control={control}
            name={'inventoryImage'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseUploadImage
                  label={t('inventory_form_title_product_photos')}
                  uploadedFiles={value}
                  onChange={(files: IBaseFile[]) => onChange(files)}
                  onRemove={handleRemoveFile}
                  errorMessage={errors.inventoryImage?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            control={control}
            name={'inventoryFile'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseUploadDocument
                  label={t('inventory_form_title_info_sheets')}
                  uploadedFiles={value}
                  onChange={(files: IBaseFile[]) => onChange(files)}
                  onRemove={handleRemoveFile}
                  errorMessage={errors.inventoryFile?.message}
                />
              );
            }}
          />
        </Grid2>
      </Grid2>

      <Typography className={cx('header')}>{t('inventory_form_title_pricing_details')}</Typography>

      <Grid2 id='infoInventoryFormComponent' columnSpacing={6} rowSpacing={'15px'} container>
        {!inventoryId && (
          <Grid2 size={6}>
            <Controller
              control={control}
              name={'quantity'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('inventory_form_title_inventory_quantity')}
                    placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
                    value={String(value)}
                    onChange={(e) => {
                      const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                      const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
                      onChange(String(numericValue));
                    }}
                    errorMessage={errors?.quantity?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>
        )}

        {!inventoryId && (
          <Grid2 size={6}>
            <Controller
              control={control}
              name={'rentalPrice'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('inventory_form_title_rental_price')}
                    placeholder={t('common_text_placeholder_money', { ns: 'common/base' })}
                    value={value}
                    onChange={(event) => {
                      onChange(formatInputCurrency(event.target.value));
                    }}
                    errorMessage={errors?.rentalPrice?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>
        )}

        <Grid2 size={6}>
          <Controller
            control={control}
            name={'purchasePrice'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('inventory_form_title_purchase_price')}
                  placeholder={t('common_text_placeholder_money', { ns: 'common/base' })}
                  value={value}
                  onChange={(event) => {
                    onChange(formatInputCurrency(event.target.value));
                  }}
                  errorMessage={errors?.purchasePrice?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            control={control}
            name={'replacementCosts'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('inventory_form_title_replacement_cost')}
                  placeholder={t('common_text_placeholder_money', { ns: 'common/base' })}
                  value={value}
                  onChange={(event) => {
                    onChange(formatInputCurrency(event.target.value));
                  }}
                  errorMessage={errors?.replacementCosts?.message}
                />
              );
            }}
          />
        </Grid2>

        {inventoryId && (
          <Grid2 size={6}>
            <Controller
              control={control}
              name={'reason'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('inventory_form_title_reason')}
                    placeholder={t('inventory_form_title_select_reason')}
                    callApiFn={fetchOptionsReason}
                    value={value}
                    onChange={(data) => {
                      onChange(data?.value);
                      setValue('purchaseDate', EMPTY_STRING);
                      setValue('purchaseCost', EMPTY_STRING);
                    }}
                    errorMessage={errors?.reason?.message}
                  />
                );
              }}
            />
          </Grid2>
        )}

        {reason === ReasonType.PURCHASED_NEW_PRODUCT && (
          <>
            <Grid2 size={4}>
              <Controller
                control={control}
                name={'purchaseDate'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseDatePicker
                      mode='date'
                      height={36}
                      label={t('inventory_form_title_purchase_date')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.purchaseDate?.message}
                      required
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={4}>
              <Controller
                control={control}
                name={'purchaseCost'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      height={36}
                      label={t('inventory_form_title_purchase_cost')}
                      placeholder={t('common_text_placeholder_money', { ns: 'common/base' })}
                      value={value}
                      onChange={(event) => {
                        onChange(formatInputCurrency(event.target.value));
                      }}
                      errorMessage={errors?.purchaseCost?.message}
                    />
                  );
                }}
              />
            </Grid2>
          </>
        )}
      </Grid2>

      <Typography className={cx('header')}>{t('inventory_form_title_time_capacity')}</Typography>

      <Grid2 id='infoInventoryFormComponent' columnSpacing={6} rowSpacing={'15px'} container>
        <Grid2 size={6}>
          <Controller
            control={control}
            name={'timeLoading'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('inventory_form_title_enter_time_loading')}
                  placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
                  value={value}
                  onChange={(e) => {
                    const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                    const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
                    onChange(String(numericValue));
                  }}
                  errorMessage={errors?.timeLoading?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            control={control}
            name={'timeUnloading'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('inventory_form_title_enter_time_unloading')}
                  placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
                  value={value}
                  onChange={(e) => {
                    const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                    const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
                    onChange(String(numericValue));
                  }}
                  errorMessage={errors?.timeUnloading?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            control={control}
            name={'timeDelivery'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('inventory_form_title_enter_time_delivery')}
                  placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
                  value={value}
                  onChange={(e) => {
                    const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                    const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
                    onChange(String(numericValue));
                  }}
                  errorMessage={errors?.timeDelivery?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            control={control}
            name={'timePickup'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('inventory_form_title_enter_time_pickup')}
                  placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
                  value={value}
                  onChange={(e) => {
                    const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                    const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
                    onChange(String(numericValue));
                  }}
                  errorMessage={errors?.timePickup?.message}
                />
              );
            }}
          />
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default InfoInventoryForm;
